import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { appService } from "../services/service";

const APP_KEY = ["app-overview"];
const OWNER_DASHBOARD_KEY = ["owner-dashboard"];
const SELLER_DASHBOARD_KEY = ["seller-dashboard"];

export const useAppOverview = () => {
    const [selectedYear, setSelectedYear] = useState(
        new Date().getFullYear().toString(),
    );
    const [selectedMonth, setSelectedMonth] = useState(
        new Date().getMonth() + 1,
    );
    const [timeRange, setTimeRange] = useState("monthly");

    const params = useMemo(() => ({
        year: selectedYear,
        month: selectedMonth,
        range: timeRange,
    }), [selectedYear, selectedMonth, timeRange]);

    const query = useQuery({
        queryKey: [...APP_KEY, params],
        queryFn: () => appService.getOverview(params),
        staleTime: Infinity,
    });

    const payload = query.data?.data?.data ?? query.data?.data ?? null;

    return {
        stats: payload?.stats || {
            total_users: 0,
            total_shops: 0,
            total_stock: 0,
            total_sales: 0,
        },
        chart: payload?.chart || [],
        recentSales: payload?.recent_sales || [],
        availableYears: payload?.available_years || [],

        loading: query.isLoading || query.isFetching,
        error: query.error,
        refresh: query.refetch,

        selectedYear,
        setSelectedYear,
        selectedMonth,
        setSelectedMonth,
        timeRange,
        setTimeRange,
    };
};

export const useOwnerDashboard = () => {
    const [selectedYear, setSelectedYear] = useState(
        new Date().getFullYear().toString(),
    );
    const [selectedMonth, setSelectedMonth] = useState(
        new Date().getMonth() + 1,
    );
    const [timeRange, setTimeRange] = useState("monthly");
    const [shopId, setShopId] = useState("");

    const [activitiesPage, setActivitiesPage] = useState(1);
    const [activitiesPerPage, setActivitiesPerPage] = useState(5);
    const [activitiesSearch, setActivitiesSearch] = useState("");

    const params = useMemo(() => ({
        year: selectedYear,
        month: selectedMonth,
        range: timeRange,
        shopId: shopId || undefined,
        activities_page: activitiesPage,
        activities_per_page: activitiesPerPage,
        activities_search: activitiesSearch || undefined,
    }), [
        selectedYear,
        selectedMonth,
        timeRange,
        shopId,
        activitiesPage,
        activitiesPerPage,
        activitiesSearch,
    ]);

    const query = useQuery({
        queryKey: [...OWNER_DASHBOARD_KEY, params],
        queryFn: () => appService.getOwnerDashboard(params),
        staleTime: Infinity,
    });

    const payload = query.data?.data?.data ?? query.data?.data ?? null;

    return {
        cards: payload?.cards || {
            total_purchase: 0,
            total_stock_count: 0,
            total_sales: 0,
            total_closeout: 0,
        },
        chart: payload?.chart || [],
        recentActivities: payload?.recent_activities || [],
        recentActivitiesMeta: payload?.recent_activities_meta || {
            total: 0,
            last_page: 1,
            per_page: 5,
            current_page: 1,
        },
        categoryDistribution: payload?.category_distribution || [],
        businessPerformance: payload?.business_performance || {
            total_profit: 0,
            total_loss: 0,
            capital: 0,
            earnings: 0,
            shop_name: "N/A",
            shop_status: "inactive",
        },
        topProducts: payload?.top_products || [],
        availableYears: payload?.available_years || [],
        availableShops: payload?.available_shops || [],
        selectedShopId: payload?.selected_shop_id || null,

        loading: query.isLoading || query.isFetching,
        error: query.error,
        refresh: query.refetch,

        selectedYear,
        setSelectedYear,
        selectedMonth,
        setSelectedMonth,
        timeRange,
        setTimeRange,
        shopId,
        setShopId,

        activitiesPage,
        setActivitiesPage,
        activitiesPerPage,
        setActivitiesPerPage,
        activitiesSearch,
        setActivitiesSearch,
    };
};

export const useSellerDashboard = () => {
    const [selectedYear, setSelectedYear] = useState(
        new Date().getFullYear().toString(),
    );
    const [selectedMonth, setSelectedMonth] = useState(
        new Date().getMonth() + 1,
    );
    const [timeRange, setTimeRange] = useState("monthly");
    const [shopId, setShopId] = useState("");

    const [activitiesPage, setActivitiesPage] = useState(1);
    const [activitiesPerPage, setActivitiesPerPage] = useState(5);
    const [activitiesSearch, setActivitiesSearch] = useState("");

    const params = useMemo(() => ({
        year: selectedYear,
        month: selectedMonth,
        range: timeRange,
        shopId: shopId || undefined,
        activities_page: activitiesPage,
        activities_per_page: activitiesPerPage,
        activities_search: activitiesSearch || undefined,
    }), [
        selectedYear,
        selectedMonth,
        timeRange,
        shopId,
        activitiesPage,
        activitiesPerPage,
        activitiesSearch,
    ]);

    const query = useQuery({
        queryKey: [...SELLER_DASHBOARD_KEY, params],
        queryFn: () => appService.getSellerDashboard(params),
        staleTime: Infinity,
    });

    const payload = query.data?.data?.data ?? query.data?.data ?? null;

    return {
        cards: payload?.cards || {
            total_products: 0,
            total_shops: 0,
            total_sales: 0,
            total_closeout: 0,
        },
        chart: payload?.chart || [],
        recentActivities: payload?.recent_activities || [],
        recentActivitiesMeta: payload?.recent_activities_meta || {
            total: 0,
            last_page: 1,
            per_page: 5,
            current_page: 1,
        },
        categoryDistribution: payload?.category_distribution || [],
        availableYears: payload?.available_years || [],
        availableShops: payload?.available_shops || [],
        selectedShopId: payload?.selected_shop_id || null,

        loading: query.isLoading || query.isFetching,
        error: query.error,
        refresh: query.refetch,

        selectedYear,
        setSelectedYear,
        selectedMonth,
        setSelectedMonth,
        timeRange,
        setTimeRange,
        shopId,
        setShopId,

        activitiesPage,
        setActivitiesPage,
        activitiesPerPage,
        setActivitiesPerPage,
        activitiesSearch,
        setActivitiesSearch,
    };
};